import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Bot, Copy, Globe, Loader2, Newspaper, Rocket } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { absolutizeUrl } from "@/entities/cs-web-entry";
import { toast } from "@/shared/hooks/use-toast";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { errorText } from "@/shared/lib/apiErrors";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { UrlQrPreview } from "@/shared/ui/UrlQrPreview";
import { cn } from "@/shared/lib/utils";

import {
  flowService,
  type LaunchChat,
  type LaunchNewsRequest,
  type LaunchView,
  type NewsGenre,
  type NewsStatus,
} from "../api/flowService";
import { chatModeText, NEWS_GENRES } from "../model/flowLabels";
import { domainStatusText, previewHost } from "../model/siteLabels";
import { isLaunched, sitePath } from "../model/siteStep";
import type { DomainOverallStatus } from "../api/types";
import { copyText } from "./components/copyText";
import { NEWS_MANAGE_PATH } from "./components/flow/NewsStatusEntry";
import { HostPreflightBlockers } from "./components/host/HostPreviewCard";
import { NO_NEWS_LIST_HINT } from "./components/TemplateCapabilities";
import { useCanManageSite } from "./hooks/useCanManageSite";
import { useDraftEditor } from "./hooks/useDraftEditor";
import { useHostPublish } from "./hooks/useHostPublish";
import { SiteShell } from "./SiteShell";

const card = cn("space-y-3 border border-border bg-card p-4", CARD_WALL_RADIUS);

/** 宿主自动挂浮窗脚本(chatWidget)还没上,这句如实写。 */
export const CHAT_WIDGET_TEXT = "官网每个页面右下角会挂上 AI 客服浮窗";

/**
 * ③ 上线(1010,一页三卡片 + 正式上线):
 *  - 绑定自有域名(复用域名页,可跳过 —— 免费子域名本来就能访问);
 *  - 新闻自动更新:开关 + 节奏 + 默认体裁 → 为本站创建 / 启用新闻舱「官网栏目」;已有栏目显示状态与「去管理」;
 *  - 官网 AI 客服:一键建客服 H5 接入点「官网 · {站名}」并把咨询类按钮链接改成它;浮窗待宿主;
 *  - 「正式上线」= 现有发布(体检 → 确认 → publish,MANAGER+)。
 */
export default function LaunchPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const canManage = useCanManageSite();
  const ed = useDraftEditor(id);
  const hp = useHostPublish(ed);
  const [view, setView] = useState<LaunchView | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setViewError(null);
    try {
      setView(await flowService.launch(id));
    } catch (e) {
      setViewError(errorText(e));
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const { site } = ed;
  if (ed.error) {
    return <QueryErrorBanner isError message={ed.error} onRetry={() => void ed.load()} />;
  }
  if (!site) return <Skeleton className="h-64 w-full" />;

  const launched = isLaunched(site);
  const publish = async () => {
    await hp.publish();
    void load();
  };

  return (
    <SiteShell
      site={site}
      pageTitle="上线"
      beforeLeave={ed.confirmLeave}
      subtitle={
        launched
          ? `已上线 · 线上 v${site.currentVersionNo ?? "-"}`
          : `预览地址 ${previewHost(site.previewUrl)} 已可访问;配好下面三项后正式上线`
      }
      actions={
        canManage && (
          <Button size="sm" disabled={hp.publishing || ed.saving} onClick={() => void publish()}>
            {hp.publishing ? <Loader2 className="animate-spin" /> : <Rocket />}
            {launched ? "发布最新改动" : "正式上线"}
          </Button>
        )
      }
    >
      <HostPreflightBlockers errors={hp.blockers} onLocate={() => navigate(sitePath(id, "edit"))} />
      <QueryErrorBanner
        isError={!!viewError}
        message={viewError ?? undefined}
        onRetry={() => void load()}
      />
      {!view && !viewError && <Skeleton className="h-40 w-full" />}
      {view && (
        <div className="grid gap-4 lg:grid-cols-3">
          <DomainCard siteId={id} view={view} />
          <NewsCard
            siteId={id}
            news={view.news}
            onChange={(news) => setView((v) => (v ? { ...v, news } : v))}
          />
          <ChatCard
            siteId={id}
            chat={view.chat}
            onChange={(chat) => setView((v) => (v ? { ...v, chat } : v))}
          />
        </div>
      )}
      {!canManage && (
        <p className="text-2xs text-muted-foreground">正式上线需要管理员或经理操作。</p>
      )}
    </SiteShell>
  );
}

function Card({ label, icon, children }: { label: string; icon: ReactNode; children: ReactNode }) {
  return (
    <section aria-label={label} className={card}>
      <h2 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
        {icon}
        {label}
      </h2>
      {children}
    </section>
  );
}

function DomainCard({ siteId, view }: { siteId: string; view: LaunchView }) {
  const d = view.domain;
  return (
    <Card label="绑定自有域名" icon={<Globe className="h-4 w-4 text-primary" aria-hidden />}>
      <p className="text-xs text-muted-foreground">
        免费子域名 <b className="text-foreground">{previewHost(view.site.previewUrl)}</b>{" "}
        已经能访问,绑定自有域名可以跳过。
      </p>
      {d.bound && d.domain ? (
        <p className="text-xs" data-domain-bound="">
          <b>{d.domain}</b>
          {d.status && (
            <span className="ml-1 text-muted-foreground">
              {domainStatusText(d.status as DomainOverallStatus, false) || d.status}
            </span>
          )}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">还没绑定自有域名。</p>
      )}
      <Button asChild size="sm" variant="outline">
        <Link to={sitePath(siteId, "domain")}>{d.bound ? "管理域名" : "去绑定"}</Link>
      </Button>
    </Card>
  );
}

function NewsCard({
  siteId,
  news,
  onChange,
}: {
  siteId: string;
  news: NewsStatus;
  onChange: (n: NewsStatus) => void;
}) {
  const [cadence, setCadence] = useState<"daily" | "weekly">("daily");
  const [perWeek, setPerWeek] = useState(3);
  const [genre, setGenre] = useState<NewsGenre>(
    (NEWS_GENRES.find((g) => g.value === news.genre)?.value ?? "COMPANY_NEWS") as NewsGenre
  );
  const [publishTime, setPublishTime] = useState(news.publishTime ?? "09:00");
  const [busy, setBusy] = useState(false);

  const submit = async (body: LaunchNewsRequest) => {
    setBusy(true);
    try {
      const n = await flowService.setNews(siteId, body);
      onChange(n);
      toast({ title: body.enabled === false ? "新闻自动更新已暂停" : "新闻自动更新已开启" });
    } catch (e) {
      toast({ title: "没设置成功", description: errorText(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const icon = <Newspaper className="h-4 w-4 text-primary" aria-hidden />;
  if (!news.moduleEnabled) {
    return (
      <Card label="新闻自动更新" icon={icon}>
        <p className="text-xs text-muted-foreground">
          新闻舱未开通,开通后可让官网每天 / 每周自动更新文章。
        </p>
      </Card>
    );
  }
  const on = news.state === "enabled";
  return (
    <Card label="新闻自动更新" icon={icon}>
      {/* 「没有新闻列表」只说一处(1010 演示机:api 的 hint 与前端兜底话术同义,出现过两遍):
          articleList=false 用 api 的 hint(没有才用兜底),橙色;其它情况 hint 灰色 */}
      {news.articleList === false ? (
        <p role="note" className="text-2xs leading-relaxed text-warning" data-news-hint="">
          {news.hint || NO_NEWS_LIST_HINT}
        </p>
      ) : (
        news.hint && (
          <p className="text-2xs text-muted-foreground" data-news-hint="">
            {news.hint}
          </p>
        )
      )}
      {on ? (
        <div className="space-y-2" data-news-state="enabled">
          <p className="text-sm text-foreground">已开启 · {news.label}</p>
          {news.nextPlannedAt && (
            <p className="text-2xs text-muted-foreground">
              下一篇计划 {news.nextPlannedAt.replace("T", " ").slice(0, 16)}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to={NEWS_MANAGE_PATH}>去管理</Link>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void submit({ enabled: false })}
            >
              暂停
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-xs" data-news-state={news.state}>
          <p className="text-muted-foreground">
            {news.state === "disabled" ? "已暂停" : "未开启"}
            。开启后新闻舱按节奏写稿,审过后发到官网。
          </p>
          <div role="radiogroup" aria-label="节奏" className="flex gap-1">
            {(["daily", "weekly"] as const).map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={cadence === c}
                onClick={() => setCadence(c)}
                className={cn(
                  "rounded-md border px-2.5 py-1",
                  cadence === c ? "border-primary bg-primary/10 text-primary" : "border-border"
                )}
              >
                {c === "daily" ? "每天 1 篇" : "每周"}
              </button>
            ))}
            {cadence === "weekly" && (
              <select
                aria-label="每周篇数"
                className="h-7 rounded-md border border-input bg-background px-1"
                value={perWeek}
                onChange={(e) => setPerWeek(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <option key={n} value={n}>
                    {n} 篇
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              aria-label="默认体裁"
              className="h-7 rounded-md border border-input bg-background px-1"
              value={genre}
              onChange={(e) => setGenre(e.target.value as NewsGenre)}
            >
              {NEWS_GENRES.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
            <input
              type="time"
              aria-label="发布时间"
              className="h-7 rounded-md border border-input bg-background px-1"
              value={publishTime}
              onChange={(e) => setPublishTime(e.target.value)}
            />
          </div>
          <Button
            size="sm"
            disabled={busy}
            onClick={() =>
              void submit({
                enabled: true,
                cadence,
                ...(cadence === "weekly" ? { perWeek } : {}),
                publishTime,
                genre,
              })
            }
          >
            {busy && <Loader2 className="animate-spin" />}
            {news.state === "disabled" ? "恢复" : "开启"}
          </Button>
        </div>
      )}
    </Card>
  );
}

function ChatCard({
  siteId,
  chat,
  onChange,
}: {
  siteId: string;
  chat: LaunchChat;
  onChange: (c: LaunchChat) => void;
}) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  const unlink = async () => {
    const ok = await confirm({
      title: "取消接入官网客服?",
      description: "站点上改过的咨询按钮会恢复原链接,访客不再进入这个客服接入点。",
      confirmText: "取消接入",
      variant: "warning",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await flowService.unlinkChat(siteId);
      onChange(
        r && typeof r === "object" && "state" in r
          ? r
          : {
              ...chat,
              state: "none",
              entryId: null,
              entryName: null,
              h5Url: null,
              linkedButtons: 0,
              mode: null,
              note: null,
            }
      );
      toast({ title: "已取消接入官网客服" });
    } catch (e) {
      toast({ title: "没取消成功", description: errorText(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };
  const icon = <Bot className="h-4 w-4 text-primary" aria-hidden />;

  const create = async () => {
    setBusy(true);
    try {
      onChange(await flowService.linkChat(siteId));
      toast({ title: "官网客服已接好" });
    } catch (e) {
      toast({ title: "没接成功", description: errorText(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  if (!chat.moduleEnabled) {
    return (
      <Card label="官网 AI 客服" icon={icon}>
        <p className="text-xs text-muted-foreground">AI 客服未开通,开通后可一键给官网接上客服。</p>
      </Card>
    );
  }
  if (chat.state !== "linked") {
    return (
      <Card label="官网 AI 客服" icon={icon}>
        <p className="text-xs text-muted-foreground">
          一键为本站创建客服接入点,并把站点上「在线咨询 / 联系我们」类按钮指向它。
          {CHAT_WIDGET_TEXT}。
        </p>
        <Button size="sm" disabled={busy} onClick={() => void create()}>
          {busy && <Loader2 className="animate-spin" />}
          一键接入客服
        </Button>
      </Card>
    );
  }
  const url = chat.h5Url ? absolutizeUrl(chat.h5Url) : "";
  return (
    <Card label="官网 AI 客服" icon={icon}>
      <div className="space-y-2 text-xs" data-chat-state="linked">
        <p className="text-foreground">已接入 · {chat.entryName ?? "官网客服"}</p>
        {chatModeText(chat) && <p className="text-muted-foreground">{chatModeText(chat)}</p>}
        {chat.note && <p className="text-muted-foreground">{chat.note}</p>}
        {url ? (
          <div className="flex flex-wrap items-start gap-3">
            <div className="rounded-md border border-border bg-white p-1.5" data-chat-qr="">
              <UrlQrPreview url={url} size={112} />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <p className="text-2xs text-muted-foreground">手机扫码试试,或复制链接:</p>
              <p className="break-all font-mono text-2xs">{url}</p>
              <Button size="sm" variant="outline" onClick={() => void copyText(url, "客服链接")}>
                <Copy />
                复制链接
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-muted-foreground">客服链接还没生成,稍后刷新本页。</p>
        )}
        {/* 浮窗这句只说一处:api 的 note 已经说了(1010 演示机出现过两遍)就不再补 */}
        {!chat.note?.includes("浮窗") && (
          <p className="text-2xs text-muted-foreground">{CHAT_WIDGET_TEXT}。</p>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          disabled={busy}
          onClick={() => void unlink()}
        >
          {busy && <Loader2 className="animate-spin" />}
          取消接入
        </Button>
      </div>
    </Card>
  );
}
